import { Fragment } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { loadGlossaryEntry } from "~/utils/content-fns";
import { TEAM_AUTHOR_KEY, authorPath } from "~/utils/authors";
import { seo, seoLinks, articleJsonLd, breadcrumbJsonLd, definedTermJsonLd, faqJsonLd } from "~/utils/seo";
import { BlogNewsletterSidebar } from "~/components/BlogNewsletterSidebar";
import { TableOfContents } from "~/components/TableOfContents";
import type { RelatedPost } from "~/types/views";

export const Route = createFileRoute("/glossary/$slug")({
  loader: ({ params }) => loadGlossaryEntry({ data: { slug: params.slug } }),
  head: ({ loaderData }) => ({
    meta: loaderData
      ? seo({
          title: loaderData.seo_title || loaderData.title,
          description: loaderData.seo_description || loaderData.description,
          image: loaderData.featured_image,
          url: `/glossary/${loaderData.slug}`,
        })
      : [],
    links: loaderData ? seoLinks({ url: `/glossary/${loaderData.slug}` }) : [],
    // Structured data is opt-in per entry: DefinedTerm/FAQPage only when the
    // frontmatter carries `alternate_names` / `faq`. Authors are not modelled
    // for glossary, so Article credits the Organization.
    scripts:
      loaderData && loaderData.robots !== "noindex" && (loaderData.alternateNames.length > 0 || loaderData.faq.length > 0)
        ? [
            definedTermJsonLd({
              url: `/glossary/${loaderData.slug}`,
              name: loaderData.termName ?? loaderData.title,
              description: loaderData.description,
              alternateNames: loaderData.alternateNames,
            }),
            articleJsonLd({
              url: `/glossary/${loaderData.slug}`,
              title: loaderData.title,
              description: loaderData.description,
              image: loaderData.featured_image,
              datePublished: loaderData.date,
              dateModified: loaderData.last_updated,
              authors: loaderData.authors
                .filter((a) => a.key !== TEAM_AUTHOR_KEY)
                .map((a) => ({ name: a.name, url: a.hasPage ? authorPath(a.key) : null })),
            }),
            faqJsonLd(loaderData.faq),
            breadcrumbJsonLd([
              { name: "Glossary", url: "/glossary" },
              { name: loaderData.title },
            ]),
          ].filter((s) => s !== null)
        : [],
  }),
  component: GlossaryEntry,
});

function RelatedPosts({ posts }: { posts: RelatedPost[] }) {
  if (posts.length === 0) return null;
  return (
    <div className="mx-auto max-w-screen-xl pb-24 pt-20 md:py-28">
      <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">Explore more posts</h2>
      <div className="mt-6 grid gap-5 md:mt-9 md:grid-cols-3">
        {posts.map((p) => (
          <div key={p.slug} className="relative overflow-hidden rounded-md border border-[#DCDADA] bg-white shadow-card">
            {p.featured_image && (
              <div className="relative block aspect-[1200/630]">
                <img src={p.featured_image} alt={p.title} className="h-full w-full object-cover" loading="lazy" />
              </div>
            )}
            <div className="p-6 lg:p-7">
              <h3 className="font-body text-sm font-bold leading-tight text-charcoal md:text-lg">{p.title}</h3>
            </div>
            <Link to="/glossary/$slug" params={{ slug: p.slug }} className="absolute inset-0" />
          </div>
        ))}
      </div>
    </div>
  );
}

function GlossaryEntry() {
  const entry = Route.useLoaderData();
  const dateFormatted = new Date(entry.date).toLocaleDateString("en-US", {
    weekday: "short", year: "numeric", month: "short", day: "numeric",
  });

  const updatedFormatted = entry.last_updated
    ? new Date(`${entry.last_updated.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", {
        year: "numeric", month: "short", day: "numeric", timeZone: "UTC",
      })
    : null;

  return (
    <div itemScope itemType="http://schema.org/Article">
      <div className="relative">
        <div className="mt-10 px-4 md:px-8">
          <div className="mx-auto max-w-screen-xl">
            {/* Hero card — glossary uses shadow-guide */}
            <div className="relative z-30 grid items-center overflow-hidden rounded-2xl border border-[#DCDADA] bg-white/90 shadow-guide lg:grid-cols-2">
              <div className="p-5 lg:px-12 lg:py-10">
                {/* Type label — plain text above the title, matching blog posts */}
                <div className="flex items-center space-x-3">
                  <span className="font-body text-base capitalize text-charcoal">glossary</span>
                </div>
                <h1 className="mt-4 font-display text-3xl font-bold leading-12 text-charcoal md:text-4xl md:leading-12" itemProp="headline name">
                  {entry.title}
                </h1>
                <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  {entry.authors.length > 0 && (
                    <span className="font-body text-gray-60">
                      {"By "}
                      {entry.authors.map((a, i) => (
                        <Fragment key={a.key}>
                          {i > 0 && ", "}
                          {a.hasPage ? (
                            <a href={authorPath(a.key)} rel="author" className="underline underline-offset-2 hover:text-teal-dark">
                              {a.name}
                            </a>
                          ) : (
                            a.name
                          )}
                        </Fragment>
                      ))}
                    </span>
                  )}
                  <time className="font-body text-gray-60" itemProp="datePublished" content={new Date(entry.date).toISOString().slice(0, 10)}>
                    {dateFormatted}
                  </time>
                  {updatedFormatted && (
                    <time className="font-body text-gray-60" itemProp="dateModified" dateTime={entry.last_updated ?? undefined}>
                      Last updated {updatedFormatted}
                    </time>
                  )}
                </div>
              </div>
              <div className="block h-full overflow-hidden px-6 pb-8 pt-3 lg:rounded-l-xl lg:p-0">
                {entry.featured_image && (
                  <img src={entry.featured_image} alt={entry.title} itemProp="image" className="rounded-md lg:h-full lg:object-cover lg:object-center" />
                )}
              </div>
            </div>
          </div>

          {/* Sidebar + Content */}
          <div className="mx-auto mt-10 flex max-w-screen-xl flex-col md:flex-row md:gap-8 xl:gap-24">
            <div className="flex-none md:w-[17.18rem] md:pt-4">
              <div className="sticky md:top-28">
                <TableOfContents entries={entry.toc} />
                <div className="mt-8 hidden md:block">
                  <BlogNewsletterSidebar />
                </div>
              </div>
            </div>
            <div className="content flex-1 font-body text-gray-80" itemProp="articleBody" dangerouslySetInnerHTML={{ __html: entry.html }} />
          </div>

          <div className="mt-20 md:hidden">
            <BlogNewsletterSidebar />
          </div>

          <RelatedPosts posts={entry.relatedPosts} />
        </div>
      </div>
    </div>
  );
}
